/**
 * Model Settings Tab - טאב הגדרות מודל AI
 * Manage site-wide default model and view available models with pricing
 */

import { useEffect, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { RefreshCw, Check, Cpu, DollarSign, AlertTriangle } from 'lucide-react'

export default function ModelSettingsTab() {
  const {
    models,
    modelsLoading,
    modelsError,
    currentDefaultModel,
    fetchModels,
    setDefaultModel,
    budget,
    budgetLoading,
    budgetError,
    fetchBudget,
    setMonthlyBudget
  } = useAdminStore()

  const [selectedModel, setSelectedModel] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')

  const [budgetInput, setBudgetInput] = useState<string>('')
  const [budgetSaving, setBudgetSaving] = useState(false)
  const [budgetSuccessMsg, setBudgetSuccessMsg] = useState('')

  useEffect(() => {
    fetchModels()
    fetchBudget()
  }, [fetchModels, fetchBudget])

  useEffect(() => {
    if (budget) {
      setBudgetInput(String(budget.monthlyBudgetUSD))
    }
  }, [budget])

  const handleBudgetSave = async () => {
    const value = Number(budgetInput)
    if (!Number.isFinite(value) || value < 0 || value > 10000) return
    if (budget && value === budget.monthlyBudgetUSD) return
    setBudgetSaving(true)
    setBudgetSuccessMsg('')
    try {
      await setMonthlyBudget(value)
      setBudgetSuccessMsg('התקציב החודשי עודכן בהצלחה')
      setTimeout(() => setBudgetSuccessMsg(''), 3000)
    } catch {
      // error surfaced in store
    } finally {
      setBudgetSaving(false)
    }
  }

  const formatResetDate = (iso?: string) => {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  useEffect(() => {
    if (currentDefaultModel) {
      setSelectedModel(currentDefaultModel)
    }
  }, [currentDefaultModel])

  const handleSave = async () => {
    if (!selectedModel || selectedModel === currentDefaultModel) return
    setSaving(true)
    setSuccessMsg('')
    try {
      await setDefaultModel(selectedModel)
      setSuccessMsg('מודל ברירת המחדל עודכן בהצלחה')
      setTimeout(() => setSuccessMsg(''), 3000)
    } finally {
      setSaving(false)
    }
  }

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`
  }

  const getModelShortName = (id: string): string => {
    if (id.includes('opus-4-6')) return 'Claude Opus 4.6'
    if (id.includes('sonnet-4-6')) return 'Claude Sonnet 4.6'
    if (id.includes('opus-4-')) return 'Claude Opus 4'
    if (id.includes('sonnet-4-5')) return 'Claude Sonnet 4.5'
    if (id.includes('sonnet-4-')) return 'Claude Sonnet 4'
    if (id.includes('haiku-4-5')) return 'Claude Haiku 4.5'
    if (id.includes('3-5-sonnet')) return 'Claude 3.5 Sonnet'
    if (id.includes('3-5-haiku')) return 'Claude 3.5 Haiku'
    if (id.includes('3-opus')) return 'Claude 3 Opus'
    if (id.includes('3-sonnet')) return 'Claude 3 Sonnet'
    if (id.includes('3-haiku')) return 'Claude 3 Haiku'
    return id
  }

  const budgetPct = budget ? Math.min(100, budget.percentUsed) : 0
  const barColor = !budget
    ? 'bg-gray-200'
    : budget.isOverBudget
      ? 'bg-red-500'
      : budget.percentUsed >= 80
        ? 'bg-amber-500'
        : 'bg-green-500'

  return (
    <div className="space-y-8">
      {/* Monthly Budget */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <DollarSign className="text-primary-600" size={24} aria-hidden="true" />
            <h2 className="text-xl font-bold text-gray-900">תקציב חודשי ל-AI</h2>
          </div>
          <button
            onClick={() => fetchBudget()}
            disabled={budgetLoading}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <RefreshCw size={16} className={budgetLoading ? 'animate-spin' : ''} aria-hidden="true" />
            רענן
          </button>
        </div>

        <p className="text-gray-600 text-sm mb-4">
          תקרת הוצאה חודשית על קריאות לשירות ה-AI. כשהשימוש מגיע לתקרה, שיחות חדשות נחסמות עד תחילת החודש הבא (UTC).
        </p>

        {budgetError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" role="alert">
            {budgetError}
          </div>
        )}

        {budgetSuccessMsg && (
          <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-4 flex items-center gap-2">
            <Check size={16} aria-hidden="true" />
            {budgetSuccessMsg}
          </div>
        )}

        {budget && budget.isOverBudget && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg text-sm mb-4 flex items-start gap-2" role="alert">
            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <div className="font-semibold">התקציב החודשי הסתיים — שיחות AI חסומות</div>
              <div className="text-xs mt-1">
                ההגבלה תתאפס ב-{formatResetDate(budget.resetsAt)}. ניתן להעלות את התקציב כדי להתיר שיחות מיידית.
              </div>
            </div>
          </div>
        )}

        {/* Usage bar */}
        {budget && (
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-700 font-medium">
                שימוש החודש: ${budget.currentSpendUSD.toFixed(2)} מתוך ${budget.monthlyBudgetUSD.toFixed(2)}
              </span>
              <span className={`font-mono ${budget.isOverBudget ? 'text-red-600' : 'text-gray-500'}`}>
                {budget.percentUsed.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                style={{ width: `${budgetPct}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-2">
              איפוס הבא: {formatResetDate(budget.resetsAt)}
            </div>
          </div>
        )}

        {/* Edit budget */}
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-xs">
            <label htmlFor="monthly-budget-input" className="block text-sm font-medium text-gray-700 mb-1">
              תקציב חודשי (דולר)
            </label>
            <div className="relative">
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">$</span>
              <input
                id="monthly-budget-input"
                type="number"
                dir="ltr"
                min={0}
                max={10000}
                step={1}
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                disabled={budgetLoading || budgetSaving}
                className="w-full rounded-lg border border-gray-300 ps-8 pe-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <button
            onClick={handleBudgetSave}
            disabled={
              budgetSaving ||
              !budgetInput ||
              !Number.isFinite(Number(budgetInput)) ||
              (budget !== null && Number(budgetInput) === budget.monthlyBudgetUSD)
            }
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
          >
            {budgetSaving ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </div>

      {/* Current Default */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Cpu className="text-primary-600" size={24} aria-hidden="true" />
            <h2 className="text-xl font-bold text-gray-900">מודל ברירת מחדל</h2>
          </div>
          <button
            onClick={() => fetchModels()}
            disabled={modelsLoading}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <RefreshCw size={16} className={modelsLoading ? 'animate-spin' : ''} aria-hidden="true" />
            רענן
          </button>
        </div>

        <p className="text-gray-600 text-sm mb-4">
          המודל הנבחר ישמש את כל המשתמשים במערכת, אלא אם הוגדר מודל ייעודי למשתמש ספציפי.
        </p>

        {modelsError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" role="alert">
            {modelsError}
          </div>
        )}

        {successMsg && (
          <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-4 flex items-center gap-2">
            <Check size={16} aria-hidden="true" />
            {successMsg}
          </div>
        )}

        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label htmlFor="default-model-select" className="block text-sm font-medium text-gray-700 mb-1">
              בחר מודל
            </label>
            <select
              id="default-model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={modelsLoading}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">-- בחר מודל --</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.id})
                  {m.pricing ? ` — $${m.pricing.inputPricePerMillion}/$${m.pricing.outputPricePerMillion} per 1M` : ''}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !selectedModel || selectedModel === currentDefaultModel}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
          >
            {saving ? 'שומר...' : 'שמור'}
          </button>
        </div>

        {currentDefaultModel && (
          <p className="text-xs text-gray-500 mt-2">
            מודל נוכחי: <span className="font-mono">{currentDefaultModel}</span>
          </p>
        )}
      </div>

      {/* Models Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">מודלים זמינים</h2>
          <p className="text-sm text-gray-500 mt-1">
            רשימה חיה מ-Anthropic API • מחירים לכל מיליון טוקנים
          </p>
        </div>

        {modelsLoading && models.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2" aria-hidden="true" />
            טוען מודלים...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">שם</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">מזהה</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">מחיר קלט</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">מחיר פלט</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">סטטוס</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {models.map((model) => {
                  const isDefault = model.id === currentDefaultModel
                  return (
                    <tr
                      key={model.id}
                      className={`hover:bg-gray-50 transition-colors ${isDefault ? 'bg-primary-50' : ''}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {getModelShortName(model.id)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                        {model.id}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {model.pricing ? formatPrice(model.pricing.inputPricePerMillion) : (
                          <span className="text-gray-400">לא ידוע</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {model.pricing ? formatPrice(model.pricing.outputPricePerMillion) : (
                          <span className="text-gray-400">לא ידוע</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isDefault ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                            <Check size={12} aria-hidden="true" />
                            ברירת מחדל
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {!modelsLoading && models.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            לא נמצאו מודלים. ודא שמפתח ה-API תקין.
          </div>
        )}
      </div>
    </div>
  )
}
